import { inject, Injectable } from "@angular/core";
import { Actions } from "@ngrx/effects";
import { ChannelApi } from "./channel.api";
import { SnackbarService } from "../../core/services";
import * as ChannelActions from "./channel.actions";
import * as ChatSocketActions from "../websocket/websocket.actions";
import { createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, of, switchMap } from "rxjs";


@Injectable()

export class ChannelEffects {
    private readonly actions$ = inject(Actions);
    private readonly channelApi = inject(ChannelApi)
    private readonly snackbar = inject(SnackbarService);

    createChannel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelCreateRequest),
            switchMap(({ payload }) =>
                this.channelApi.createChannel(payload).pipe(
                    map((response) => ChannelActions.ChannelCreateSuccess({ channel: response.data })),
                    catchError(error => {
                        if (error.status === 422) {
                            return of((ChannelActions.ChannelValidatoionError({ validationError: error.error.errors })));
                        }
                        return of(ChannelActions.ChannelCreateFailure({ error: error.message }));
                    })
                )
            )
        )
    );

    createChannelSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelCreateSuccess),
            map(() => {
                
            })
        ),
        { dispatch: false }
    );

    createChannelFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelCreateFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to create channel: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    fetchChannel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelFetchRequest),
            switchMap(() =>
                this.channelApi.getChannel().pipe(
                    map((response) => ChannelActions.ChannelFetchSuccess({ channels: response.data })),
                    catchError(error => of(ChannelActions.ChannelFetchFailure({ error: error.message })))
                )
            )
        )
    );

    fetchChannelSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelFetchSuccess),
            map(() => {
                // this.snackbar.showNotification('success', '', 'Channels fetched successfully', 'Close');
            })
        ),
        { dispatch: false }
    );

    fetchChannelFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelFetchFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to fetch channels: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    refreshAndSubscribeOnChannelCreated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChatSocketActions.receiveChannelCreated),
            mergeMap(({ channelCreated }) => [
                ChannelActions.ChannelFetchRequest(),
                ChatSocketActions.subscribeChannel({ channelId: channelCreated.channel_id }),
            ])
        )
    );

    markMessagesRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelMarkMessagesReadRequest),
            switchMap(({ payload }) =>
                this.channelApi.markMessagesRead(payload).pipe(
                    map((response) => ChannelActions.ChannelMarkMessagesReadSuccess({ message: response.message })),
                    catchError(error => of(ChannelActions.ChannelMarkMessagesReadFailure({ error: error.message })))
                )
            )
        )
    );

    markMessagesReadSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelMarkMessagesReadSuccess),
            map(() => ChannelActions.ChannelFetchRequest())
        )
    );

    markMessagesReadFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelMarkMessagesReadFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to mark messages as read: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    leaveChannel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelLeaveRequest),
            switchMap(({ payload }) =>
                this.channelApi.leaveChannel(payload).pipe(
                    map((response) => ChannelActions.ChannelLeaveSuccess({ channelId: payload.channel_id, message: response.message })),
                    catchError(error => of(ChannelActions.ChannelLeaveFailure({ error: error.message })))
                )
            )
        )
    );

    leaveChannelSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelLeaveSuccess),
            mergeMap(({ channelId }) => [
                ChannelActions.ChannelFetchRequest(),
                ChatSocketActions.unsubscribeChannel({ channelId }),
            ])
        )
    );

    leaveChannelSuccessNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelLeaveSuccess),
            map(({ message }) => {
                this.snackbar.showNotification('success', '', message, 'Close');
            })
        ),
        { dispatch: false }
    );

    leaveChannelFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelLeaveFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to leave channel: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    addParticipant$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelAddParticipantRequest),
            switchMap(({ payload }) =>
                this.channelApi.addParticipant(payload).pipe(
                    map((response) => ChannelActions.ChannelAddParticipantSuccess({ channelId: payload.channel_id, userId: payload.user_id, message: response.message })),
                    catchError(error => of(ChannelActions.ChannelAddParticipantFailure({ error: error.message })))
                )
            )
        )
    );

    addParticipantSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelAddParticipantSuccess),
            map(() => ChannelActions.ChannelFetchRequest())
        )
    );

    addParticipantSuccessNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelAddParticipantSuccess),
            map(({ message }) => {
                this.snackbar.showNotification('success', '', message, 'Close');
            })
        ),
        { dispatch: false }
    );

    addParticipantFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelAddParticipantFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to add user to channel: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    renameChannel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelRenameRequest),
            switchMap(({ payload }) =>
                this.channelApi.renameChannel(payload).pipe(
                    map((response) => ChannelActions.ChannelRenameSuccess({ channel: response.data, message: response.message })),
                    catchError(error => of(ChannelActions.ChannelRenameFailure({ error: error.error?.message || error.message })))
                )
            )
        )
    );

    renameChannelSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelRenameSuccess),
            map(() => ChannelActions.ChannelFetchRequest())
        )
    );

    renameChannelSuccessNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelRenameSuccess),
            map(({ message }) => {
                this.snackbar.showNotification('success', '', message, 'Close');
            })
        ),
        { dispatch: false }
    );

    renameChannelFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChannelActions.ChannelRenameFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to rename group chat: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );


}
